import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { StatusBadge } from '../components/ui/StatusBadge'
import ProgressBar from '../components/ui/ProgressBar'
import FileUpload from '../components/files/FileUpload'
import FileList from '../components/files/FileList'
import Comments from '../components/files/Comments'
import AIReportButton from '../components/ai/AIReportButton'
import Modal from '../components/ui/Modal'
import { ArrowLeft, ChevronRight, FileText, MessageSquare, BarChart3, Pencil, Trash2, Plus, Trophy, Save, Edit3, Download, Loader, Building2, Target } from 'lucide-react'
import pptxgen from 'pptxgenjs'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

// Icons for BU stages (stages 4-8 of the methodology: 5 stages per BU)
const STAGE_ICONS = ['📊','👥','⚔️','🏢','🗺️']

export default function BusinessUnitPage() {
  const { id, buId } = useParams()
  const navigate = useNavigate()
  const { isAdmin, isNVC } = useAuth()
  const [bu, setBu]               = useState(null)
  const [consultancy, setConsultancy] = useState(null)
  const [phases, setPhases]       = useState([])
  const [loading, setLoading]     = useState(true)
  const [activeTab, setActiveTab] = useState('stages')

  // BU edit modal
  const [editOpen, setEditOpen]   = useState(false)
  const [buForm, setBuForm]       = useState({ name: '', description: '' })
  const [saving, setSaving]       = useState(false)

  // New stage modal
  const [stageOpen, setStageOpen] = useState(false)
  const [stageForm, setStageForm] = useState({ name: '', description: '' })
  const [savingStage, setSavingStage] = useState(false)

  const [exportingPPT, setExportingPPT] = useState(false)

  // Resultado
  const [resultText, setResultText]         = useState('')
  const [savedResult, setSavedResult]       = useState('')
  const [editingResult, setEditingResult]   = useState(false)
  const [savingResult, setSavingResult]     = useState(false)
  const [resultRecordId, setResultRecordId] = useState(null)
  const [resultFileKey, setResultFileKey]   = useState(0)

  useEffect(() => { fetchData() }, [buId])

  async function fetchData() {
    setLoading(true)
    const [{ data: buData }, { data: cons }, { data: phs }, { data: resultRecord }] = await Promise.all([
      supabase.from('business_units').select('*').eq('id', buId).single(),
      supabase.from('consultancies').select('name, client_company').eq('id', id).single(),
      supabase.from('phases')
        .select('*, sections(id, status)')
        .eq('business_unit_id', buId)
        .order('phase_order'),
      supabase.from('ai_reports').select('id, content')
        .eq('entity_type', 'business_unit').eq('entity_id', buId)
        .eq('report_type', 'resultado').maybeSingle(),
    ])
    setBu(buData)
    setConsultancy(cons)
    setPhases(phs ?? [])
    if (buData) setBuForm({ name: buData.name, description: buData.description ?? '' })
    if (resultRecord) {
      setSavedResult(resultRecord.content ?? '')
      setResultText(resultRecord.content ?? '')
      setResultRecordId(resultRecord.id)
    }
    setLoading(false)
  }

  async function saveResult() {
    if (!resultText.trim()) return
    setSavingResult(true)
    if (resultRecordId) {
      await supabase.from('ai_reports').update({ content: resultText }).eq('id', resultRecordId)
    } else {
      const { data } = await supabase.from('ai_reports').insert({
        consultancy_id: id,
        entity_type: 'business_unit',
        entity_id: buId,
        report_type: 'resultado',
        content: resultText,
      }).select().single()
      if (data) setResultRecordId(data.id)
    }
    setSavedResult(resultText)
    setEditingResult(false)
    setSavingResult(false)
  }

  async function saveBuEdit(e) {
    e.preventDefault()
    setSaving(true)
    await supabase.from('business_units').update(buForm).eq('id', buId)
    setBu(b => ({ ...b, ...buForm }))
    setEditOpen(false)
    setSaving(false)
  }

  async function deleteBu() {
    if (!confirm('¿Eliminar esta unidad de negocio y todas sus etapas? Esta acción no se puede deshacer.')) return
    await supabase.from('business_units').delete().eq('id', buId)
    navigate(`/consultancy/${id}`)
  }

  async function createStage(e) {
    e.preventDefault()
    setSavingStage(true)
    const nextOrder = phases.length + 1
    await supabase.from('phases').insert({
      business_unit_id: buId,
      consultancy_id: id,
      name: stageForm.name,
      description: stageForm.description || null,
      phase_order: nextOrder,
      status: 'pending',
      progress: 0,
    })
    setStageOpen(false)
    setStageForm({ name: '', description: '' })
    fetchData()
    setSavingStage(false)
  }

  async function deleteStage(phaseId) {
    if (!confirm('¿Eliminar esta etapa y todas sus secciones? Esta acción no se puede deshacer.')) return
    await supabase.from('phases').delete().eq('id', phaseId)
    setPhases(prev => prev.filter(p => p.id !== phaseId))
  }

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="animate-spin rounded-full h-10 w-10 border-4 border-brand-500 border-t-transparent" />
    </div>
  )
  if (!bu) return <div className="text-center py-20 text-gray-400">Unidad de negocio no encontrada</div>

  const overallProgress = phases.length
    ? Math.round(phases.reduce((s, p) => s + (p.progress ?? 0), 0) / phases.length)
    : 0

  const tabs = [
    { id: 'stages',    label: 'Etapas',       icon: BarChart3,     special: false },
    { id: 'resultado', label: 'Resultado',    icon: Trophy,        special: true  },
    { id: 'files',     label: 'Archivos',     icon: FileText,      special: false },
    { id: 'comments',  label: 'Comentarios',  icon: MessageSquare, special: false },
  ]

  return (
    <div className="space-y-6">
      {/* Breadcrumb — labeled with entity type. Current = UN with chip. */}
      <div className="flex items-center gap-1.5 text-sm text-gray-400 flex-wrap">
        <button onClick={() => navigate('/')} className="hover:text-brand-600 transition-colors">Dashboard</button>
        <ChevronRight size={14} className="flex-shrink-0" />
        <button onClick={() => navigate(`/consultancy/${id}`)}
          className="inline-flex items-center gap-1.5 hover:text-brand-600 transition-colors truncate max-w-[180px] md:max-w-none">
          <Building2 size={13} className="opacity-60" />
          <span className="text-[10px] uppercase tracking-wide font-semibold opacity-60">Proyecto:</span>
          <span className="truncate">{consultancy?.client_company ?? 'Consultoría'}</span>
        </button>
        <ChevronRight size={14} className="flex-shrink-0" />
        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-brand-50 border border-brand-200 text-brand-700 font-semibold truncate max-w-[200px] md:max-w-none">
          <Target size={13} />
          <span className="text-[10px] uppercase tracking-wide">UN:</span>
          <span className="truncate">{bu.name}</span>
        </span>
      </div>

      {/* Header card */}
      <div className="card">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-brand-50 border border-brand-100 flex items-center justify-center text-xl md:text-2xl flex-shrink-0">
            🏢
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <h1 className="text-lg md:text-xl font-bold text-gray-900">{bu.name}</h1>
                  <StatusBadge status={bu.status} />
                </div>
                {bu.description && <p className="text-sm text-gray-500 mt-1">{bu.description}</p>}
                <p className="text-xs text-gray-400 mt-1">{phases.length} etapas</p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                {isAdmin && (
                  <>
                    <button onClick={() => setEditOpen(true)}
                      className="p-2 text-gray-400 hover:text-brand-600 hover:bg-brand-50 rounded-lg transition-colors" title="Editar unidad">
                      <Pencil size={15} />
                    </button>
                    <button onClick={deleteBu}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Eliminar unidad">
                      <Trash2 size={15} />
                    </button>
                  </>
                )}
                <button
                  onClick={async () => {
                    setExportingPPT(true)
                    try {
                      // 1. Gather all data for this BU
                      // Find strategy/definicion phases
                      const stratPhase = phases.find(p => {
                        const n = (p.name ?? '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
                        return n.includes('estrategia') || n.includes('definicion')
                      })

                      // Fetch strategic objective + pillars
                      let stratObj = null, pillars = [], metrics = []
                      if (stratPhase) {
                        const { data: sections } = await supabase.from('sections').select('id, name').eq('phase_id', stratPhase.id)
                        const objSec = sections?.find(s => {
                          const n = (s.name ?? '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g,'')
                          return n.includes('objetivo')
                        })
                        if (objSec) {
                          const [{ data: so }, { data: pils }] = await Promise.all([
                            supabase.from('strategic_objectives').select('*').eq('section_id', objSec.id).maybeSingle(),
                            supabase.from('strategic_pillars').select('*').eq('section_id', objSec.id).order('pillar_order'),
                          ])
                          stratObj = so
                          pillars = pils ?? []
                          if (so) {
                            const { data: mx } = await supabase.from('strategic_objective_metrics').select('*').eq('strategic_objective_id', so.id).order('year')
                            metrics = mx ?? []
                          }
                        }
                      }

                      // Fetch portfolio strategy
                      const { data: allSections } = await supabase.from('sections').select('id, name').in('phase_id', phases.map(p => p.id))
                      const portSec = allSections?.find(s => {
                        const n = (s.name ?? '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g,'')
                        return n.includes('portafolio') && !n.includes('competidor')
                      })
                      let portfolio = null
                      if (portSec) {
                        const { data: p } = await supabase.from('bu_strategy_portfolio').select('*').eq('section_id', portSec.id).maybeSingle()
                        portfolio = p
                      }

                      // Fetch price strategy
                      const priceSec = allSections?.find(s => {
                        const n = (s.name ?? '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g,'')
                        return n.includes('precio')
                      })
                      let priceStrat = null
                      if (priceSec) {
                        const { data: ps } = await supabase.from('bu_price_strategy').select('*').eq('section_id', priceSec.id).maybeSingle()
                        priceStrat = ps
                      }

                      // Fetch conclusions
                      const sectionIds = allSections?.map(s => s.id) ?? []
                      const { data: conclusions } = sectionIds.length
                        ? await supabase.from('section_tab_conclusions').select('*').in('section_id', sectionIds)
                        : { data: [] }
                      const getConc = (tabId) => conclusions?.find(c => c.tab_id === tabId)?.conclusion_text

                      // ── BUILD PPT ──────────────────────────────────────
                      const pres = new pptxgen()
                      pres.layout = 'LAYOUT_WIDE'
                      const DARK = '1E1B4B'
                      const BRAND = '3730A3'
                      const LIGHT = 'EEF2FF'

                      // Slide 1 — Cover
                      const s1 = pres.addSlide()
                      s1.background = { color: DARK }
                      s1.addText(bu.name, { x:0.5, y:1.5, w:12, h:1, fontSize:32, bold:true, color:'FFFFFF' })
                      s1.addText(consultancy?.client_company ?? '', { x:0.5, y:2.7, w:12, h:0.5, fontSize:16, color:'C7D2FE' })
                      s1.addText('Reporte Estratégico Ejecutivo', { x:0.5, y:3.4, w:12, h:0.4, fontSize:13, color:'818CF8' })
                      s1.addText(new Date().toLocaleDateString('es-CO', { year:'numeric', month:'long' }), { x:0.5, y:6.8, w:12, h:0.3, fontSize:11, color:'6366F1', align:'right' })

                      // Slide 2 — BU Progress
                      const s2 = pres.addSlide()
                      s2.addText('Progreso por Etapa', { x:0.5, y:0.3, w:12, h:0.5, fontSize:20, bold:true, color: DARK })
                      const stageRows = [
                        [{ text:'Etapa', options:{bold:true,fill:LIGHT,color:BRAND} }, { text:'Estado', options:{bold:true,fill:LIGHT,color:BRAND} }, { text:'Progreso', options:{bold:true,fill:LIGHT,color:BRAND} }],
                        ...phases.map((p, i) => [
                          `${i+1}. ${p.name}`,
                          p.status === 'completed' ? '✓ Completo' : p.status === 'in_progress' ? '▶ En progreso' : '○ Pendiente',
                          `${p.progress ?? 0}%`
                        ])
                      ]
                      s2.addTable(stageRows, { x:0.5, y:0.9, w:12.3, colW:[7, 2.5, 2.8], fontSize:11, border:{type:'solid',color:'E0E7FF'}, rowH:0.45 })

                      // Slide 3 — Strategic Objective
                      if (stratObj) {
                        const s3 = pres.addSlide()
                        s3.background = { color: BRAND }
                        s3.addText('Objetivo Estratégico', { x:0.5, y:0.4, w:12, h:0.5, fontSize:20, bold:true, color:'FFFFFF' })
                        s3.addText(stratObj.declaracion ?? '', { x:0.5, y:1.1, w:12, h:3, fontSize:15, color:'E0E7FF', wrap:true })
                        if (stratObj.estrategia) {
                          s3.addText('Estrategia', { x:0.5, y:4.2, w:12, h:0.4, fontSize:12, bold:true, color:'C7D2FE' })
                          try {
                            const pts = JSON.parse(stratObj.estrategia)
                            if (Array.isArray(pts)) {
                              pts.slice(0,4).forEach((pt, i) => {
                                s3.addText(`• ${pt.texto ?? pt}`, { x:0.7, y:4.7+i*0.45, w:11.5, h:0.4, fontSize:11, color:'FFFFFF', wrap:true })
                              })
                            }
                          } catch { s3.addText(stratObj.estrategia ?? '', { x:0.7, y:4.7, w:11.5, h:1.5, fontSize:11, color:'FFFFFF', wrap:true }) }
                        }
                      }

                      // Slide 4 — Pillars
                      if (pillars.length > 0) {
                        const s4 = pres.addSlide()
                        s4.addText('Pilares Estratégicos', { x:0.5, y:0.3, w:12, h:0.5, fontSize:20, bold:true, color: DARK })
                        const pilRows = [
                          [{ text:'#', options:{bold:true,fill:LIGHT,color:BRAND} }, { text:'Pilar', options:{bold:true,fill:LIGHT,color:BRAND} }, { text:'Descripción', options:{bold:true,fill:LIGHT,color:BRAND} }, { text:'KPI', options:{bold:true,fill:LIGHT,color:BRAND} }],
                          ...pillars.map((p, i) => [String(i+1), p.name ?? '', p.description ?? '', p.kpi ?? '—'])
                        ]
                        s4.addTable(pilRows, { x:0.5, y:0.9, w:12.3, colW:[0.5, 2.8, 4.5, 4.5], fontSize:10, border:{type:'solid',color:'E0E7FF'}, rowH:0.5 })
                      }

                      // Slide 5 — Historical Metrics
                      const realMetrics = metrics.filter(m => m.type === 'real' || !m.type)
                      if (realMetrics.length > 0) {
                        const s5 = pres.addSlide()
                        s5.addText('Ventas Históricas', { x:0.5, y:0.3, w:12, h:0.5, fontSize:20, bold:true, color: DARK })
                        const mRows = [
                          [{ text:'Año', options:{bold:true,fill:LIGHT,color:BRAND} }, { text:'Ventas reales', options:{bold:true,fill:LIGHT,color:BRAND} }, { text:'Participación %', options:{bold:true,fill:LIGHT,color:BRAND} }, { text:'Objetivo pasado', options:{bold:true,fill:LIGHT,color:BRAND} }],
                          ...realMetrics.map(m => [String(m.year), m.ventas ? `${m.ventas} ${m.ventas_unit ?? ''}` : '—', m.participacion_pct ? `${m.participacion_pct}%` : '—', m.ventas_objetivo ? String(m.ventas_objetivo) : '—'])
                        ]
                        s5.addTable(mRows, { x:0.5, y:0.9, w:12.3, colW:[1.5, 3.5, 3, 4.3], fontSize:11, border:{type:'solid',color:'E0E7FF'}, rowH:0.5 })
                      }

                      // Slide 6 — Portfolio Strategy
                      if (portfolio?.products?.length > 0) {
                        const s6 = pres.addSlide()
                        s6.addText('Portafolio Estratégico', { x:0.5, y:0.3, w:12, h:0.5, fontSize:20, bold:true, color: DARK })
                        const pRows = [
                          [{ text:'Producto', options:{bold:true,fill:LIGHT,color:BRAND} }, { text:'Decisión', options:{bold:true,fill:LIGHT,color:BRAND} }, { text:'Rationale', options:{bold:true,fill:LIGHT,color:BRAND} }],
                          ...portfolio.products.map(p => [p.nombre ?? '', p.decision ?? '', p.rationale ?? ''])
                        ]
                        s6.addTable(pRows, { x:0.5, y:0.9, w:12.3, colW:[3.5, 2, 6.8], fontSize:10, border:{type:'solid',color:'E0E7FF'}, rowH:0.5 })
                        if (portfolio.new_refs?.length > 0) {
                          s6.addText(`+ ${portfolio.new_refs.length} nuevas referencias`, { x:0.5, y:6.8, w:12, h:0.3, fontSize:11, color:'6366F1' })
                        }
                      }

                      // Slide 7 — Price Strategy
                      if (priceStrat?.products?.length > 0) {
                        const s7 = pres.addSlide()
                        s7.addText('Estrategia de Precio', { x:0.5, y:0.3, w:12, h:0.5, fontSize:20, bold:true, color: DARK })
                        const prRows = [
                          [{ text:'Producto', options:{bold:true,fill:LIGHT,color:BRAND} }, { text:'Precio actual', options:{bold:true,fill:LIGHT,color:BRAND} }, { text:'Moneda', options:{bold:true,fill:LIGHT,color:BRAND} }, { text:'Posición', options:{bold:true,fill:LIGHT,color:BRAND} }],
                          ...priceStrat.products.map(p => [p.nombre ?? '', String(p.precio_actual ?? '—'), p.moneda ?? '', p.posicion ?? ''])
                        ]
                        s7.addTable(prRows, { x:0.5, y:0.9, w:12.3, colW:[4, 2.5, 2, 3.8], fontSize:11, border:{type:'solid',color:'E0E7FF'}, rowH:0.5 })
                      }

                      // Slide 8 — Executive Summary (conclusions)
                      const concObjeto = getConc('objetivo_est')
                      const concPort = getConc('portafolio_estrategia')
                      const concPrecio = getConc('precio_estrategia')
                      if (concObjeto || concPort || concPrecio) {
                        const s8 = pres.addSlide()
                        s8.addText('Conclusiones Ejecutivas', { x:0.5, y:0.3, w:12, h:0.5, fontSize:20, bold:true, color: DARK })
                        let y = 0.9
                        if (concObjeto) {
                          s8.addText('Objetivo', { x:0.5, y, w:12, h:0.3, fontSize:11, bold:true, color:BRAND })
                          s8.addText(concObjeto.slice(0,400), { x:0.5, y:y+0.3, w:12, h:1.2, fontSize:10, color:'374151', wrap:true })
                          y += 1.7
                        }
                        if (concPort) {
                          s8.addText('Portafolio', { x:0.5, y, w:12, h:0.3, fontSize:11, bold:true, color:BRAND })
                          s8.addText(concPort.slice(0,400), { x:0.5, y:y+0.3, w:12, h:1.2, fontSize:10, color:'374151', wrap:true })
                          y += 1.7
                        }
                        if (concPrecio && y < 5.5) {
                          s8.addText('Precio', { x:0.5, y, w:12, h:0.3, fontSize:11, bold:true, color:BRAND })
                          s8.addText(concPrecio.slice(0,400), { x:0.5, y:y+0.3, w:12, h:1.2, fontSize:10, color:'374151', wrap:true })
                        }
                      }

                      await pres.writeFile({ fileName: `reporte_maestro_${bu.name.replace(/\s+/g,'_')}.pptx` })
                    } catch (e) {
                      console.error('PPT Maestro error:', e)
                      alert('Error generando el PPT. Revisa la consola.')
                    }
                    setExportingPPT(false)
                  }}
                  disabled={exportingPPT}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-xs font-semibold transition-colors disabled:opacity-50 shadow-sm"
                  title="Exportar presentación ejecutiva completa"
                >
                  {exportingPPT
                    ? <><Loader size={13} className="animate-spin" /> Generando PPT...</>
                    : <><Download size={13} /> PPT Maestro</>}
                </button>
                <AIReportButton consultancyId={id} entityType="business_unit" entityId={buId} label="Informe de UdN" />
              </div>
            </div>
          </div>
        </div>
        <div className="mt-4">
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>Progreso global</span><span>{overallProgress}%</span>
          </div>
          <ProgressBar value={overallProgress} showLabel={false} />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200 overflow-x-auto flex-wrap">
        {tabs.map(({ id: tid, label, icon: Icon, special }) => (
          special ? (
            <button key={tid} onClick={() => setActiveTab(tid)}
              className={`flex items-center gap-2 px-3 md:px-4 py-2.5 text-sm font-semibold border-b-2 whitespace-nowrap transition-all ${
                activeTab === tid
                  ? 'border-emerald-500 text-emerald-700 bg-emerald-50'
                  : 'border-transparent text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50'
              }`}>
              <Icon size={15} /> {label}
              {savedResult && <span className="ml-1 w-2 h-2 rounded-full bg-emerald-500 inline-block" />}
            </button>
          ) : (
            <button key={tid} onClick={() => setActiveTab(tid)}
              className={`flex items-center gap-2 px-3 md:px-4 py-2.5 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                activeTab === tid ? 'border-brand-600 text-brand-700' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}>
              <Icon size={15} /> {label}
            </button>
          )
        ))}
      </div>

      {activeTab === 'stages' && (
        <div className="space-y-3">
          {/* Add stage button */}
          {isNVC && (
            <button onClick={() => setStageOpen(true)}
              className="btn-primary flex items-center gap-2 text-sm">
              <Plus size={16} /> Nueva Etapa
            </button>
          )}

          {phases.map((phase, idx) => (
            <StageRow
              key={phase.id}
              phase={phase}
              stageNumber={idx + 1}
              icon={STAGE_ICONS[idx] ?? '📋'}
              consultancyId={id}
              buId={buId}
              navigate={navigate}
              isNVC={isNVC}
              isAdmin={isAdmin}
              onDelete={() => deleteStage(phase.id)}
            />
          ))}

          {phases.length === 0 && (
            <div className="text-center py-16 text-gray-400">
              <BarChart3 size={40} className="mx-auto mb-3 opacity-20" />
              <p className="font-medium">No hay etapas en esta unidad</p>
              {isNVC && <p className="text-sm mt-1">Crea la primera etapa con el botón de arriba</p>}
            </div>
          )}
        </div>
      )}

      {activeTab === 'resultado' && (
        <div className="space-y-5">
          <div className="rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 px-5 py-4 flex items-center gap-3 shadow-sm">
            <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center flex-shrink-0">
              <Trophy size={18} className="text-white" />
            </div>
            <div>
              <h2 className="text-white font-semibold text-base">Resultado de la Unidad de Negocio</h2>
              <p className="text-emerald-100 text-xs mt-0.5">Documento final y análisis consolidado de esta unidad</p>
            </div>
          </div>

          <div className="card border border-emerald-100 bg-emerald-50/30">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Edit3 size={15} className="text-emerald-600" />
                <h3 className="text-sm font-semibold text-gray-800">Análisis y conclusiones</h3>
              </div>
              {!editingResult && (isAdmin || isNVC) && (
                <button onClick={() => setEditingResult(true)}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-emerald-200 text-emerald-700 hover:bg-emerald-100 transition-colors">
                  <Pencil size={12} /> {savedResult ? 'Editar' : 'Agregar análisis'}
                </button>
              )}
            </div>
            {editingResult ? (
              <div className="space-y-3">
                <textarea className="input resize-none text-sm" rows={10} value={resultText}
                  onChange={e => setResultText(e.target.value)}
                  placeholder="Escribe aquí el análisis, hallazgos y conclusiones de esta unidad de negocio. Soporta Markdown..." />
                <div className="flex gap-2">
                  <button onClick={() => { setResultText(savedResult); setEditingResult(false) }} className="btn-secondary text-sm flex-1">Cancelar</button>
                  <button onClick={saveResult} disabled={savingResult || !resultText.trim()}
                    className="text-sm flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-medium transition-colors disabled:opacity-50">
                    <Save size={14} /> {savingResult ? 'Guardando...' : 'Guardar resultado'}
                  </button>
                </div>
              </div>
            ) : savedResult ? (
              <div className="prose prose-sm max-w-none text-gray-700 prose-headings:text-gray-900 prose-headings:font-semibold prose-h2:text-base prose-h3:text-sm prose-strong:text-gray-800 prose-li:my-0.5 prose-p:my-1.5">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{savedResult}</ReactMarkdown>
              </div>
            ) : (
              <p className="text-sm text-gray-400 italic">Sin análisis agregado aún.{(isAdmin || isNVC) ? ' Haz clic en "Agregar análisis" para documentar el resultado.' : ''}</p>
            )}
          </div>

          <div className="card border border-emerald-100">
            <div className="flex items-center gap-2 mb-4">
              <FileText size={15} className="text-emerald-600" />
              <h3 className="text-sm font-semibold text-gray-800">Documentos del resultado</h3>
            </div>
            <FileUpload entityType="resultado_bu" entityId={buId} onUploaded={() => setResultFileKey(k => k + 1)} />
            <div className="mt-4"><FileList entityType="resultado_bu" entityId={buId} refreshKey={resultFileKey} /></div>
          </div>
        </div>
      )}

      {activeTab === 'files' && (
        <div className="space-y-4">
          <FileUpload entityType="business_unit" entityId={buId} onUploaded={() => {}} />
          <FileList entityType="business_unit" entityId={buId} />
        </div>
      )}

      {activeTab === 'comments' && (
        <Comments entityType="business_unit" entityId={buId} />
      )}

      {/* Edit BU modal */}
      <Modal open={editOpen} onClose={() => setEditOpen(false)} title="Editar unidad de negocio">
        <form onSubmit={saveBuEdit} className="space-y-4">
          <div>
            <label className="label">Nombre *</label>
            <input className="input" required value={buForm.name}
              onChange={e => setBuForm(f => ({ ...f, name: e.target.value }))} />
          </div>
          <div>
            <label className="label">Descripción</label>
            <textarea className="input resize-none" rows={3} value={buForm.description}
              onChange={e => setBuForm(f => ({ ...f, description: e.target.value }))} />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => setEditOpen(false)} className="btn-secondary flex-1">Cancelar</button>
            <button type="submit" disabled={saving} className="btn-primary flex-1">
              {saving ? 'Guardando...' : 'Guardar cambios'}
            </button>
          </div>
        </form>
      </Modal>

      {/* New stage modal */}
      <Modal open={stageOpen} onClose={() => setStageOpen(false)} title="Nueva Etapa">
        <form onSubmit={createStage} className="space-y-4">
          <div>
            <label className="label">Nombre de la etapa *</label>
            <input className="input" required value={stageForm.name}
              onChange={e => setStageForm(f => ({ ...f, name: e.target.value }))}
              placeholder="Ej: Análisis de Riesgos, Validación de Mercado..." />
          </div>
          <div>
            <label className="label">Descripción (opcional)</label>
            <textarea className="input resize-none" rows={3} value={stageForm.description}
              onChange={e => setStageForm(f => ({ ...f, description: e.target.value }))}
              placeholder="¿Qué se analiza en esta etapa?" />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => setStageOpen(false)} className="btn-secondary flex-1">Cancelar</button>
            <button type="submit" disabled={savingStage} className="btn-primary flex-1">
              {savingStage ? 'Creando...' : 'Crear Etapa'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  )
}

function StageRow({ phase, stageNumber, icon, consultancyId, buId, navigate, isNVC, isAdmin, onDelete }) {
  // Section counts per status. We render a multi-color stacked bar + chip
  // breakdown so the user sees not just "what's done" but also what's
  // actively in flight (in_progress, review). Previously we only counted
  // completed which made phases with several in-progress sections look
  // identical to phases that hadn't started.
  const sections = phase.sections ?? []
  const total = sections.length
  const counts = {
    completed:   sections.filter(s => s.status === 'completed').length,
    review:      sections.filter(s => s.status === 'review').length,
    in_progress: sections.filter(s => s.status === 'in_progress').length,
    blocked:     sections.filter(s => s.status === 'blocked').length,
    pending:     sections.filter(s => !s.status || s.status === 'pending').length,
  }
  // Weighted progress: completed = 1.0, review = 0.85, in_progress = 0.5
  const weighted = total > 0
    ? Math.round(((counts.completed * 1) + (counts.review * 0.85) + (counts.in_progress * 0.5)) / total * 100)
    : 0
  const widthOf = n => total > 0 ? `${(n / total) * 100}%` : '0%'

  return (
    <div className="card p-4 hover:shadow-md hover:border-brand-300 transition-all group">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(`/consultancy/${consultancyId}/bu/${buId}/phase/${phase.id}`)}
          className="flex items-center gap-4 flex-1 text-left min-w-0"
        >
          <div className="w-10 h-10 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center text-lg flex-shrink-0">
            {icon}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-bold text-gray-300">ETAPA {stageNumber}</span>
              <StatusBadge status={phase.status} />
            </div>
            <div className="flex items-center gap-2 mb-2">
              <span className="font-medium text-gray-900 group-hover:text-brand-700 transition-colors text-sm">
                {phase.name}
              </span>
            </div>
            {/* Section status chips */}
            <div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
              {counts.completed > 0 && (
                <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 font-medium">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> {counts.completed} completada{counts.completed !== 1 ? 's' : ''}
                </span>
              )}
              {counts.review > 0 && (
                <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 font-medium">
                  <span className="w-1.5 h-1.5 rounded-full bg-violet-500" /> {counts.review} en revisión
                </span>
              )}
              {counts.in_progress > 0 && (
                <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 font-medium">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-500" /> {counts.in_progress} en progreso
                </span>
              )}
              {counts.blocked > 0 && (
                <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-red-50 text-red-700 font-medium">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500" /> {counts.blocked} bloqueada{counts.blocked !== 1 ? 's' : ''}
                </span>
              )}
              {counts.pending > 0 && total > 0 && (
                <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-gray-50 text-gray-500 font-medium">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400" /> {counts.pending} por empezar
                </span>
              )}
              {total === 0 && (
                <span className="text-[11px] text-gray-400 italic">Sin secciones</span>
              )}
            </div>
            {/* Multi-color stacked progress bar */}
            <div className="flex items-center gap-3">
              <div className="flex-1 max-w-xs h-2 bg-gray-100 rounded-full overflow-hidden flex">
                <div className="bg-emerald-500 h-full transition-all" style={{ width: widthOf(counts.completed) }} />
                <div className="bg-violet-500 h-full transition-all"  style={{ width: widthOf(counts.review) }} />
                <div className="bg-blue-500 h-full transition-all"    style={{ width: widthOf(counts.in_progress) }} />
                <div className="bg-red-400 h-full transition-all"     style={{ width: widthOf(counts.blocked) }} />
              </div>
              <span className="text-xs text-gray-500 tabular-nums">{weighted}%</span>
            </div>
          </div>
          <ChevronRight size={18} className="text-gray-300 group-hover:text-brand-400 flex-shrink-0 transition-colors" />
        </button>

        {/* Delete button */}
        {isAdmin && (
          <button
            onClick={e => { e.stopPropagation(); onDelete() }}
            className="p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
            title="Eliminar etapa"
          >
            <Trash2 size={15} />
          </button>
        )}
      </div>
    </div>
  )
}
